import React, { useState, useCallback, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ContentPasteIcon from "@mui/icons-material/ContentPaste";
import { useDropzone } from "react-dropzone";
import Icon from "./Icons";
import QrCodeHandwrittenPrescriptionUpload from "./QrCodeHandwrittenPrescriptionUpload";
import axios from "axios";

const PrescriptionUploadModal = ({
  open,
  handleClose,
  handleSubmitAdd,
  files,
  setFiles,
  patientId,
  authToken,
  facilityName,
  orgCode: orgCodeProp,
  appMode: appModeProp,
  backendUrl,
  isAiEnabled: isAiEnabledProp,
}) => {
  const orgCode = orgCodeProp ?? sessionStorage.getItem("ORG_CODE") ?? "";
  const appMode = appModeProp ?? sessionStorage.getItem("APP_MODE") ?? "";

  const isAiEnabled =
    typeof isAiEnabledProp === "boolean"
      ? isAiEnabledProp
      : sessionStorage.getItem("isAiEnabled") !== undefined &&
          sessionStorage.getItem("isAiEnabled") !== null
        ? JSON.parse(sessionStorage.getItem("isAiEnabled"))
        : false;

  // "Clipboard" picker for already uploaded handwritten prescriptions
  const [rxPickerOpen, setRxPickerOpen] = useState(false);
  const [rxLoading, setRxLoading] = useState(false);
  const [rxError, setRxError] = useState("");
  const [rxDocuments, setRxDocuments] = useState([]);
  const [rxSelectedS3Key, setRxSelectedS3Key] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [ocrMode, setOcrMode] = useState(
    isAiEnabled ? "with_ocr" : "without_ocr"
  );

  const apiBase = backendUrl || process.env.REACT_APP_BACKEND_ROUTE_URL || "";

  useEffect(() => {
    setOcrMode(isAiEnabled ? "with_ocr" : "without_ocr");
  }, [open, isAiEnabled]);

  const getAuthHeaders = useCallback(() => {
    const tokenHdr = authToken || sessionStorage.getItem("accessToken") || "";
    const mode =
      sessionStorage.getItem("APP_MODE") ||
      sessionStorage.getItem("appMode") ||
      "";
    const org =
      sessionStorage.getItem("ORG_CODE") || sessionStorage.getItem("orgCode") || "";
    const username =
      sessionStorage.getItem("userName") ||
      sessionStorage.getItem("username") ||
      "";
    const accessKey =
      sessionStorage.getItem("USER_ACCESS_KEY") ||
      sessionStorage.getItem("accessKey") ||
      "";

    return {
      Authorization: `Bearer ${tokenHdr}`,
      "Content-Type": "application/json",
      "App-Mode": mode,
      "Org-Code": org,
      Username: username,
      "User-Access-Key": accessKey,
    };
  }, [authToken]);

  const guessMimeType = (fileName) => {
    const lower = (fileName || "").toLowerCase();
    if (lower.endsWith(".pdf")) return "application/pdf";
    if (lower.endsWith(".png")) return "image/png";
    if (lower.endsWith(".jpg") || lower.endsWith(".jpeg")) return "image/jpeg";
    return "application/octet-stream";
  };

  /** Build File from API `file_base64` for add_hw_prescription (multipart `file`). */
  const fileFromBase64 = (base64, fileName, contentType) => {
    const clean = (base64 || "").replace(/\s/g, "");
    if (!clean) throw new Error("Empty file_base64");
    const binary = atob(clean);
    const len = binary.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i += 1) bytes[i] = binary.charCodeAt(i);
    const mime =
      contentType && contentType !== "binary/octet-stream"
        ? contentType
        : guessMimeType(fileName);
    const blob = new Blob([bytes], { type: mime });
    return new File([blob], fileName || "prescription", { type: mime });
  };

  const fetchRxDocuments = useCallback(async () => {
    if (!patientId) return;

    setRxLoading(true);
    setRxError("");
    setRxDocuments([]);
    try {
      const response = await axios.post(
        `${apiBase}get-patient-uploaded-documents/`,
        {
          patient_id: patientId,
          upload_type: "prescription",
          include_base64: true,
        },
        { headers: getAuthHeaders() }
      );

      if (response.data?.status === "Success") {
        setRxDocuments(response.data?.documents || []);
      } else {
        setRxError(
          response.data?.message || "Failed to fetch prescriptions"
        );
      }
    } catch (e) {
      console.error("Rx fetch error:", e);
      setRxError("Failed to fetch prescriptions");
    } finally {
      setRxLoading(false);
    }
  }, [patientId, apiBase, getAuthHeaders]);

  const handlePickRx = useCallback(
    (doc) => {
      if (!doc?.url && !doc?.file_base64) return;
      const mimeType =
        doc.content_type && doc.content_type !== "binary/octet-stream"
          ? doc.content_type
          : guessMimeType(doc.file_name);
      let preview = null;
      if (mimeType.startsWith("image/") && doc.file_base64) {
        preview = `data:${mimeType};base64,${doc.file_base64}`;
      } else if (mimeType.startsWith("image/") && doc.url) {
        preview = doc.url;
      }
      setRxSelectedS3Key(doc.s3_key || null);
      setFiles([
        {
          name: doc.file_name || "prescription",
          type: mimeType,
          preview,
          remoteUrl: doc.url,
          remoteMimeType: mimeType,
          remoteS3Key: doc.s3_key || null,
          file_base64: doc.file_base64 || null,
          content_type: doc.content_type || null,
        },
      ]);
    },
    [setFiles]
  );

  const handleSubmitWithOptionalRemoteFile = useCallback(
    async () => {
      if (!files || files.length === 0) return;

      const submitOptions = {
        ocrMode: isAiEnabled ? ocrMode : "without_ocr",
      };
      const selected = files[0];
      // Clipboard RX: prefer API `file_base64` (no S3 CORS fetch).
      if (!(selected instanceof File) && selected?.file_base64) {
        setSubmitLoading(true);
        try {
          const localFile = fileFromBase64(
            selected.file_base64,
            selected.name,
            selected.content_type || selected.remoteMimeType
          );
          const mime = localFile.type;
          const preview =
            mime && mime.startsWith("image/")
              ? URL.createObjectURL(localFile)
              : null;
          const newFiles = [
            Object.assign(localFile, { preview }),
          ];
          await handleSubmitAdd(newFiles, submitOptions);
        } catch (e) {
          console.error("RX submit base64 error:", e);
        } finally {
          setSubmitLoading(false);
        }
        return;
      }
      // Fallback: presigned URL fetch (if base64 missing).
      if (selected?.remoteUrl && !(selected instanceof File)) {
        setSubmitLoading(true);
        try {
          const mimeType =
            selected.remoteMimeType || guessMimeType(selected.name);
          const resp = await fetch(selected.remoteUrl);
          if (!resp.ok) {
            throw new Error(`Failed to download selected RX (${resp.status})`);
          }
          const blob = await resp.blob();
          const localFile = new File(
            [blob],
            selected.name || "prescription",
            { type: mimeType }
          );

          const preview =
            mimeType && mimeType.startsWith("image/")
              ? URL.createObjectURL(localFile)
              : null;

          const newFiles = [
            Object.assign(localFile, {
              preview,
            }),
          ];
          await handleSubmitAdd(newFiles, submitOptions);
        } catch (e) {
          console.error("RX submit download error:", e);
        } finally {
          setSubmitLoading(false);
        }
        return;
      }

      // Normal flow: user picked a local file via dropzone.
      await handleSubmitAdd(files, submitOptions);
    },
    [files, handleSubmitAdd, ocrMode, isAiEnabled]
  );

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      setFiles([
        Object.assign(file, {
          preview: file.type.startsWith("image/")
            ? URL.createObjectURL(file)
            : null,
        }),
      ]);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "image/*": [".jpeg", ".jpg", ".png"],
      "application/pdf": [".pdf"],
    },
    multiple: false,
  });

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      {/* Title */}

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 2,
          pt: 2,
        }}
      >
        <Typography variant="h6" sx={{ color: "#125386", fontWeight: 600 }}>
          Upload Handwritten Prescription
        </Typography>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ color: "#000" }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Divider line under header */}
      <Box sx={{ borderBottom: "1px solid #C7C7CC ", mt: 1, mr: 1, ml: 1 }} />
      {/* Content */}
      <DialogContent sx={{ padding: 3, marginTop: 1 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            alignItems: { xs: "stretch", sm: "flex-start" },
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<ContentPasteIcon />}
                onClick={() => {
                  setRxPickerOpen((v) => {
                    const next = !v;
                    // Fetch only when opening
                    if (!v && next) {
                      fetchRxDocuments();
                    }
                    return next;
                  });
                }}
                sx={{ textTransform: "none" }}
              >
                Choose existing RX
              </Button>
              {rxSelectedS3Key && (
                <Typography variant="caption" sx={{ color: "#2e7d32", fontWeight: 800 }}>
                  Selected
                </Typography>
              )}
            </Box>

            <RadioGroup
              row
              value={ocrMode}
              onChange={(e) => setOcrMode(e.target.value)}
              sx={{ mb: 1.5 }}
            >
              {isAiEnabled && (
                <FormControlLabel
                  value="with_ocr"
                  control={<Radio size="small" />}
                  label="With OCR"
                  sx={{ mr: 2 }}
                />
              )}
              <FormControlLabel
                value="without_ocr"
                control={<Radio size="small" />}
                label="Without OCR"
              />
            </RadioGroup>

            {rxPickerOpen && (
              <Box
                sx={{
                  mb: 2,
                  border: "1px solid #e2e8f0",
                  borderRadius: 2,
                  p: 1.5,
                  backgroundColor: "#f8fafc",
                }}
              >
                {rxLoading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : rxError ? (
                  <Typography variant="body2" sx={{ color: "#d32f2f", fontWeight: 700 }}>
                    {rxError}
                  </Typography>
                ) : rxDocuments.length === 0 ? (
                  <Typography variant="body2" sx={{ color: "#64748b" }}>
                    No uploaded prescriptions found for this patient.
                  </Typography>
                ) : (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {rxDocuments.map((doc) => {
                      const isChosen = doc.s3_key === rxSelectedS3Key;
                      return (
                        <Box
                          key={doc.s3_key || doc.url}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 1,
                            p: 1,
                            border: "1px solid #e2e8f0",
                            borderRadius: 2,
                            backgroundColor: "#fff",
                          }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 700,
                                color: "#334155",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                maxWidth: 240,
                              }}
                            >
                              {doc.file_name || "Prescription"}
                            </Typography>
                            {doc.upload_date && (
                              <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                                {doc.upload_date}
                              </Typography>
                            )}
                          </Box>
                          <Button
                            size="small"
                            variant={isChosen ? "contained" : "outlined"}
                            onClick={() => handlePickRx(doc)}
                            sx={{ textTransform: "none" }}
                            disabled={rxLoading}
                          >
                            {isChosen ? "Chosen" : "Select"}
                          </Button>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>
            )}

            <Box
              {...getRootProps()}
              sx={{
                border: "2px dashed #1EBBAF",
                borderRadius: 2,
                padding: 3,
                textAlign: "center",
                cursor: "pointer",
                backgroundColor: isDragActive ? "#f0f8ff" : "transparent",
                backgroundImage: "url(/newImages/attachmentsBackground.svg)",
                backgroundRepeat: "no-repeat",
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              <input {...getInputProps()} />
              {isDragActive ? (
                <Typography>Drop the file here...</Typography>
              ) : (
                <Typography>
                  Drag & drop prescription file here, or click to select
                </Typography>
              )}
            </Box>

            {files.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mt: 2,
                  p: 1,
                  border: "1px solid #4DD7CD",
                  borderRadius: 1,
                  backgroundColor: "#E9FAF9",
                }}
              >
                {files[0].preview ? (
                  <img
                    src={files[0].preview}
                    alt={files[0].name}
                    style={{
                      width: 60,
                      height: 60,
                      objectFit: "cover",
                      borderRadius: "4px",
                      marginRight: "10px",
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "#eee",
                      borderRadius: "4px",
                      marginRight: "10px",
                      fontSize: "12px",
                      color: "#555",
                    }}
                  >
                    PDF
                  </Box>
                )}

                <Typography
                  variant="body2"
                  sx={{
                    flex: 1,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {files[0].name}
                </Typography>

                <IconButton onClick={() => setFiles([])} size="small">
                  <Icon name="deletePrescriptionIcon" size={24} />
                </IconButton>
              </Box>
            )}
          </Box>

          <QrCodeHandwrittenPrescriptionUpload
            patientId={patientId}
            orgCode={orgCode}
            appMode={appMode}
            facilityName={facilityName}
            authToken={authToken}
            backendUrl={backendUrl}
            active={open}
          />
        </Box>
      </DialogContent>

      {/* Actions */}
      <DialogActions sx={{ padding: 2 }}>
        <Button
          variant="contained"
          onClick={handleSubmitWithOptionalRemoteFile}
          sx={{ textTransform: "none", px: 2.5, py: 0.75 }}
          color="success"
          disabled={files.length === 0 || submitLoading}
        >
          {submitLoading ? "Submitting..." : "Submit"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PrescriptionUploadModal;
